import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Subject } from 'rxjs';
import * as DOMPurify from 'dompurify';
import {
  AlertsProps,
  AlertData,
  SUCCESS_ALERT,
  WARNING_ALERT,
  INFO_ALERT,
  ERROR_ALERT,
  AlertStatus,
  MobileAlertProps,
} from './types';
import {
  AlertBody,
  AlertBottom,
  AlertContainer,
  AlertContent,
  AlertTitle,
  AlertTop,
  ButtonContainer,
  MobileAlertContainer,
  SubMessage,
  AlertDescription,
  AlertButtonsContainer,
  CenteredContent,
} from './styles';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import { ReactComponent as SuccessIcon } from '../assets/ico-ok.svg';
import { ReactComponent as WarningIcon } from '../assets/ico-warning.svg';
import { ReactComponent as ErrorIcon } from '../assets/ico-error.svg';
import { ReactComponent as InfoIcon } from '../assets/ico-info.svg';
import { ReactComponent as SuccessIconMb } from '../assets/SuccessIcon-mb.svg';
import { ReactComponent as ErrorIconMb } from '../assets/WarningIcon-mb.svg';
import { ReactComponent as WarningIconMb } from '../assets/ErrorIcon-mb.svg';
import { ReactComponent as InfoIconMb } from '../assets/InfoIcon-mb.svg';
import { KEYS } from '../utils/constants';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';

export const $alerts = new Subject<AlertData>();

const Alerts: React.FC<AlertsProps | MobileAlertProps> = () => {
  const [alerts, setAlert] = useState<AlertData>({
    type: null,
    title: '',
  });
  const {
    isMobile,
    type,
    title,
    subMessage,
    onCancelLabel,
    onConfirmLabel,
    closeOnBlur = false,
    testId,
    closeButton = true,
    onAlertOpenChange,
  } = alerts;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => isMobile ?? mobileTheme, [isMobile, mobileTheme]);

  const showPadding = useMemo(() => (useMobileVersion && !onCancelLabel) || !useMobileVersion, [
    onCancelLabel,
    useMobileVersion,
  ]);

  const onClose = useCallback(() => {
    $alerts.next({
      type: null,
      title: '',
    });

    if (alerts.onClose) {
      alerts.onClose();
    }

    if (alerts.onAlertOpenChange) {
      alerts.onAlertOpenChange(false);
    }
  }, [alerts]);

  const goBackMobileHandler = useCallback(
    (event: any) => {
      event.detail.register(3, () => {
        onClose();
      });
    },
    [onClose],
  );

  useEffect(() => {
    if (type !== null) document.addEventListener('ionBackButton', goBackMobileHandler);
    else document.removeEventListener('ionBackButton', goBackMobileHandler);
    return () => {
      document.removeEventListener('ionBackButton', goBackMobileHandler);
    };
  }, [type, goBackMobileHandler]);

  const confirmAction = useCallback(() => {
    onClose();
    if (alerts.onConfirm) {
      alerts.onConfirm();
    }
  }, [alerts, onClose]);

  const cancelAction = useCallback(() => {
    if (alerts.onCancel) {
      alerts.onCancel();
    }

    onClose();
  }, [alerts, onClose]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ([KEYS.Enter, KEYS.NumEnter].includes(event.code as KEYS)) {
        event.stopImmediatePropagation();
        event.preventDefault();
        confirmAction();
      }
      if (event.code === KEYS.Esc) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
    },
    [confirmAction, onClose],
  );

  const handleSubMessage = useMemo(
    () => ({ __html: DOMPurify.sanitize(subMessage, { USE_PROFILES: { html: true } }) }),
    [subMessage],
  );
  useEffect(() => {
    if (type !== null) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const renderAction = useCallback(() => {
    const confirmStatus = AlertStatus[type];
    return (
      <>
        {onCancelLabel && (
          <ButtonContainer isMobile={useMobileVersion}>
            <Button
              testId={`${testId}-cancel-button`}
              hierarchy="secondary"
              label={onCancelLabel}
              status={confirmStatus}
              type="button"
              onClick={cancelAction}
              inverted={useMobileVersion}
              width={useMobileVersion ? '220' : '144'}
            />
          </ButtonContainer>
        )}
        {onConfirmLabel && (
          <ButtonContainer isMobile={useMobileVersion}>
            <Button
              testId={`${testId}-confirm-button`}
              hierarchy="primary"
              label={onConfirmLabel}
              status={confirmStatus}
              type="button"
              onClick={confirmAction}
              inverted={useMobileVersion}
              width={useMobileVersion ? '220' : '144'}
            />
          </ButtonContainer>
        )}
      </>
    );
  }, [type, onCancelLabel, useMobileVersion, testId, cancelAction, onConfirmLabel, confirmAction]);

  const renderHeader = useCallback(() => {
    const icons = {
      [SUCCESS_ALERT]: useMobileVersion ? <SuccessIconMb /> : <SuccessIcon />,
      [WARNING_ALERT]: useMobileVersion ? <WarningIconMb /> : <WarningIcon />,
      [INFO_ALERT]: useMobileVersion ? <InfoIconMb /> : <InfoIcon />,
      [ERROR_ALERT]: useMobileVersion ? <ErrorIconMb /> : <ErrorIcon />,
    };
    return type in icons ? icons[type] : '';
  }, [useMobileVersion, type]);

  useEffect(() => {
    const subscription = $alerts.subscribe((alertData) => {
      setAlert(alertData);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (type !== null && !onConfirmLabel && !onCancelLabel) {
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [type, onConfirmLabel, onCancelLabel, onClose]);

  useEffect(() => {
    if (onAlertOpenChange) {
      onAlertOpenChange(type !== null);
    }
  }, [type, onAlertOpenChange]);

  return useMobileVersion ? (
    <ThemeProvider theme={actualTheme}>
      <MobileAlertContainer bgColor={actualTheme.alerts[type]} isOpen={type !== null}>
        <CenteredContent>
          {renderHeader()}
          <AlertDescription>
            {title && (
              <AlertTitle data-testId={`${testId}-title`} isMobile={useMobileVersion}>
                {title}
              </AlertTitle>
            )}
            {subMessage && <SubMessage dangerouslySetInnerHTML={handleSubMessage} />}
          </AlertDescription>
        </CenteredContent>
        <AlertButtonsContainer
          data-testId={`${testId}-button-container`}
          isMobile={useMobileVersion}
          onCancelLabel={!!onCancelLabel}
        >
          {renderAction()}
        </AlertButtonsContainer>
      </MobileAlertContainer>
    </ThemeProvider>
  ) : (
    <ThemeProvider theme={actualTheme}>
      <Modal
        isOpen={type !== null}
        width="592px"
        height="auto"
        onClose={onClose}
        closeOnBlur={closeOnBlur}
        isMobile={useMobileVersion}
        overAll
        testId={testId}
        closeButton={closeButton}
      >
        <AlertContainer id="alerts-id" data-testid={`${testId}-container`}>
          <AlertContent paddingBottom={showPadding}>
            <AlertTop>{renderHeader()}</AlertTop>
            <AlertBottom>
              <CenteredContent>
                <AlertDescription>
                  <AlertTitle data-testId={`${testId}-title`} isMobile={useMobileVersion}>
                    {title}
                  </AlertTitle>
                  <AlertBody
                    data-testId={`${testId}-body`}
                    isMobile={useMobileVersion}
                    dangerouslySetInnerHTML={handleSubMessage}
                  />
                </AlertDescription>
              </CenteredContent>
              <AlertButtonsContainer
                data-testId={`${testId}-button-container`}
                isMobile={useMobileVersion}
                onCancelLabel={!!onCancelLabel}
              >
                {renderAction()}
              </AlertButtonsContainer>
            </AlertBottom>
          </AlertContent>
        </AlertContainer>
      </Modal>
    </ThemeProvider>
  );
};

export default Alerts;
